import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { EllipsisHorizontalIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { builderApi, errorMessage } from '../api';
import { isInputField, validateDefinition } from '../engine';
import { Eyebrow, Panel, Toggle, useManageBase } from '../ui';
import ChallengeWorkspace from './ChallengeWorkspace';

const FIELD_TYPES = [
    { type: 'short_text', label: 'Short text', hint: 'One line answer' },
    { type: 'long_text', label: 'Long text', hint: 'Paragraph answer' },
    { type: 'score', label: 'Score 1–5 + reason', hint: 'Counts toward auto total' },
    { type: 'single_choice', label: 'Single choice', hint: 'e.g. Final opinion A–D' },
    { type: 'checklist', label: 'Checklist', hint: 'All must be ticked to submit' },
    { type: 'process_steps', label: 'Process steps', hint: 'Ordered list with Start → Outcome' },
    { type: 'file_upload', label: 'File upload', hint: 'Image / PDF + consent tick' },
    { type: 'number', label: 'Number', hint: 'With min / max rule' },
    { type: 'checkbox', label: 'Tick box', hint: 'Yes/no; can unlock other questions' },
    { type: 'date', label: 'Date', hint: 'Calendar date' },
    { type: 'heading', label: 'Sub-heading', hint: 'Starts a new card in the section' },
    { type: 'info', label: 'Guidance text', hint: 'Read-only help for students' }
];
const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map((t) => [t.type, t.label]));

const inputClass = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20';

function Field({ label, children, hint }) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs font-semibold text-ink-800">{label}</span>
            {children}
            {hint && <span className="mt-1 block text-[11px] text-ink-500">{hint}</span>}
        </label>
    );
}

function slug(text) {
    return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24);
}

function newFieldKey(sectionKey, type, existing) {
    const base = `${slug(sectionKey) || 's'}_${type}`;
    let key;
    do {
        key = `${base}_${Math.random().toString(36).slice(2, 6)}`;
    } while (existing.has(key));
    return key;
}

function defaultField(type, sectionKey, existing) {
    if (type === 'heading') return { type, title: 'New sub-heading', description: '' };
    if (type === 'info') return { type, title: 'Guidance', body: '', bullets: [] };
    const field = { key: newFieldKey(sectionKey, type, existing), type, label: TYPE_LABEL[type], required: true, allow_na: false };
    if (type === 'score') Object.assign(field, { label: 'New area', allow_na: true, levels: { 1: 'Very low — ', 3: 'Moderate — ', 5: 'High — ' } });
    if (type === 'single_choice') Object.assign(field, { label: 'Choose one', display: 'cards', columns: 2, options: [{ value: 'A', label: 'Option A', description: '' }, { value: 'B', label: 'Option B', description: '' }] });
    if (type === 'checklist') Object.assign(field, { label: 'Checklist', items: ['First item'] });
    if (type === 'file_upload') Object.assign(field, { label: 'Supporting files', required: false, accept: 'image_pdf', max_files: 5, consent_label: 'No confidential data shown', consent_scope: 'each' });
    if (type === 'process_steps') field.label = 'Process map';
    return field;
}

function nextSectionKey(sections) {
    const used = new Set(sections.map((s) => s.key));
    for (let i = 0; i < 26; i += 1) {
        const k = String.fromCharCode(65 + i);
        if (!used.has(k)) return k;
    }
    return String(sections.length + 1);
}

function move(list, from, to) {
    if (to < 0 || to >= list.length || from === to) return list;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
}

export default function ChallengeBuilder() {
    const { id } = useParams();
    const { base, area } = useManageBase();
    const [draft, setDraft] = useState(null);
    const [saved, setSaved] = useState(null);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(null);
    const [previewing, setPreviewing] = useState(false);

    useEffect(() => {
        builderApi.get(id)
            .then((c) => { setDraft(c); setSaved(JSON.stringify(c)); })
            .catch((err) => setError(errorMessage(err, 'Could not load this challenge')));
    }, [id]);

    const dirty = draft && saved !== JSON.stringify(draft);
    useEffect(() => {
        const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
    const updateSettings = (patch) => setDraft((d) => ({ ...d, settings: { ...d.settings, ...patch } }));
    const updateSection = (index, patch) => setDraft((d) => ({ ...d, sections: d.sections.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));

    const persist = async (publish) => {
        const problems = validateDefinition(draft);
        if (problems.length) {
            toast.error(problems[0]);
            return;
        }
        setBusy(publish === true ? 'publish' : publish === false ? 'unpublish' : 'save');
        try {
            const next = await builderApi.update(id, draft, publish);
            setDraft(next);
            setSaved(JSON.stringify(next));
            toast.success(publish === true ? 'Challenge published' : publish === false ? 'Challenge unpublished' : 'Draft saved');
        } catch (err) {
            toast.error(errorMessage(err, 'Could not save the challenge'));
        } finally {
            setBusy(null);
        }
    };

    if (error) return <Panel><p className="text-sm text-red-700">{error}</p></Panel>;
    if (!draft) return <div className="h-96 animate-pulse rounded-2xl bg-ink-100" />;

    if (previewing) {
        return <ChallengeWorkspace previewChallenge={{ ...draft, number: draft.order_index || 1 }} onExitPreview={() => setPreviewing(false)} />;
    }

    const section = draft.sections[selected] || draft.sections[0];
    const sectionIndex = draft.sections.indexOf(section);

    return (
        <div className="mx-auto w-full max-w-6xl space-y-5 pb-16">
            <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <Eyebrow>
                        <Link to={base} className="underline decoration-ink-300 underline-offset-4 hover:text-brand-700">{area} / Practical challenges</Link> / Edit
                    </Eyebrow>
                    <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">Challenge builder</h1>
                    <p className="mt-1 text-sm text-ink-600">Build any challenge from sections and fields — no code change needed for new challenges.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
                    <button type="button" onClick={() => setPreviewing(true)} className="btn-secondary">Preview as student</button>
                    <button type="button" onClick={() => persist()} disabled={Boolean(busy)} className="btn-secondary">{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
                    {draft.is_published ? (
                        <>
                            <button type="button" onClick={() => persist(false)} disabled={Boolean(busy)} className="btn-secondary">{busy === 'unpublish' ? 'Unpublishing…' : 'Unpublish'}</button>
                            <button type="button" onClick={() => persist(true)} disabled={Boolean(busy)} className="btn-primary">{busy === 'publish' ? 'Publishing…' : 'Publish changes'}</button>
                        </>
                    ) : (
                        <button type="button" onClick={() => persist(true)} disabled={Boolean(busy)} className="btn-primary">{busy === 'publish' ? 'Publishing…' : 'Publish challenge'}</button>
                    )}
                </div>
            </header>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
                <div className="space-y-5">
                    <SettingsPanel draft={draft} update={update} updateSettings={updateSettings} />
                    <SectionsPanel
                        sections={draft.sections}
                        selected={sectionIndex}
                        onSelect={setSelected}
                        onReorder={(from, to) => { update({ sections: move(draft.sections, from, to) }); setSelected(to); }}
                        onAdd={() => {
                            const key = nextSectionKey(draft.sections);
                            update({ sections: [...draft.sections, { key, title: 'New section', fields: [] }] });
                            setSelected(draft.sections.length);
                        }}
                    />
                    <CopyPanel draft={draft} update={update} />
                </div>

                {section ? (
                    <SectionEditor
                        key={sectionIndex}
                        section={section}
                        draft={draft}
                        onChange={(patch) => updateSection(sectionIndex, patch)}
                        onDelete={() => {
                            if (draft.sections.length === 1) { toast.error('A challenge needs at least one section'); return; }
                            if (!window.confirm(`Delete section ${section.key} · ${section.title}? Answers already saved for its questions will no longer be shown.`)) return;
                            update({ sections: draft.sections.filter((_, i) => i !== sectionIndex) });
                            setSelected(Math.max(0, sectionIndex - 1));
                        }}
                    />
                ) : <Panel>Add a section to start.</Panel>}
            </div>
        </div>
    );
}

function SettingsPanel({ draft, update, updateSettings }) {
    const s = draft.settings || {};
    return (
        <Panel>
            <h2 className="text-[15px] font-semibold text-ink-950">Settings</h2>
            <div className="mt-4 space-y-3.5">
                <Field label="Title"><input className={inputClass} value={draft.title} onChange={(e) => update({ title: e.target.value })} /></Field>
                <Field label="Card summary"><textarea rows={2} className={inputClass} value={draft.summary || ''} onChange={(e) => update({ summary: e.target.value })} /></Field>
                <Field label="Linked stage / module"><input className={inputClass} value={draft.stage_label || ''} onChange={(e) => update({ stage_label: e.target.value })} placeholder="Stage 3 · Take AI beyond the classroom" /></Field>
                <Field label="Format">
                    <select className={inputClass} value={draft.layout} onChange={(e) => update({ layout: e.target.value })}>
                        <option value="single">Single page</option>
                        <option value="workbook">Multi-part workbook</option>
                    </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Due date"><input type="date" className={inputClass} value={s.due_date || ''} onChange={(e) => updateSettings({ due_date: e.target.value || null })} /></Field>
                    <Field label="Order"><input type="number" min={0} className={inputClass} value={draft.order_index ?? 0} onChange={(e) => update({ order_index: Number(e.target.value) })} /></Field>
                </div>
            </div>
            <div className="mt-3 divide-y divide-border border-t border-border">
                <Toggle label="Mentor approval required" checked={Boolean(s.mentor_approval_required)} onChange={(v) => updateSettings({ mentor_approval_required: v })} />
                <Toggle label="Unlock next stage on approval" checked={Boolean(s.unlock_next_on_approval)} onChange={(v) => updateSettings({ unlock_next_on_approval: v })} />
                <Toggle label="Show auto score to student" checked={Boolean(s.show_auto_score)} onChange={(v) => updateSettings({ show_auto_score: v })} />
                <Toggle label="Allow resubmission" checked={Boolean(s.allow_resubmission)} onChange={(v) => updateSettings({ allow_resubmission: v })} />
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-ink-500">Allow resubmission lets an intern recall a submission that is still waiting for review.</p>
        </Panel>
    );
}

function SectionsPanel({ sections, selected, onSelect, onReorder, onAdd }) {
    const dragFrom = useRef(null);
    return (
        <Panel>
            <h2 className="text-[15px] font-semibold text-ink-950">Sections</h2>
            <ul className="mt-3 space-y-1">
                {sections.map((s, i) => (
                    <li
                        key={`${s.key}-${i}`}
                        draggable
                        onDragStart={() => { dragFrom.current = i; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => { onReorder(dragFrom.current, i); dragFrom.current = null; }}
                    >
                        <button
                            type="button" onClick={() => onSelect(i)}
                            className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${i === selected ? 'bg-brand-50 font-semibold text-brand-800' : 'text-ink-800 hover:bg-ink-50'}`}
                        >
                            <span className="cursor-grab text-ink-300" aria-hidden="true">⋮⋮</span>
                            <span className="truncate">{s.key} · {s.title}</span>
                            <span className="ml-auto text-[11px] font-normal text-ink-400">{(s.fields || []).filter(isInputField).length}</span>
                        </button>
                    </li>
                ))}
            </ul>
            <button type="button" onClick={onAdd} className="mt-3 w-full rounded-lg border border-border py-2 text-sm font-semibold text-ink-900 hover:border-ink-400">+ Add section</button>
        </Panel>
    );
}

function CopyPanel({ draft, update }) {
    const instructionsText = (draft.instructions || []).map((b) => [
        `# ${b.title || ''}`,
        ...(b.paragraphs || []),
        ...(b.bullets || []).map((x) => `- ${x}`)
    ].join('\n')).join('\n\n');
    const [text, setText] = useState(instructionsText);

    const parseInstructions = (value) => {
        setText(value);
        const blocks = [];
        let current = null;
        for (const raw of value.split('\n')) {
            const line = raw.trimEnd();
            if (line.startsWith('# ')) {
                current = { title: line.slice(2).trim(), paragraphs: [], bullets: [] };
                blocks.push(current);
            } else if (line.trim()) {
                if (!current) { current = { title: '', paragraphs: [], bullets: [] }; blocks.push(current); }
                if (line.startsWith('- ')) current.bullets.push(line.slice(2).trim());
                else current.paragraphs.push(line.trim());
            }
        }
        update({ instructions: blocks });
    };

    return (
        <details className="rounded-2xl border border-border bg-white p-5">
            <summary className="cursor-pointer text-[15px] font-semibold text-ink-950">Student-facing copy</summary>
            <div className="mt-4 space-y-3.5">
                <Field label="Intro line"><textarea rows={2} className={inputClass} value={draft.lead || ''} onChange={(e) => update({ lead: e.target.value })} /></Field>
                <Field label="Card highlights" hint="Comma separated, e.g. 5 people incl. you, 10+ minutes">
                    <input className={inputClass} value={(draft.highlights || []).join(', ')} onChange={(e) => update({ highlights: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} />
                </Field>
                <Field label="Card detail" hint="e.g. Interview: 30–45 min"><input className={inputClass} value={draft.meta_label || ''} onChange={(e) => update({ meta_label: e.target.value })} /></Field>
                <Field label="“The minimum” boxes" hint="One per line: value | label">
                    <textarea
                        rows={3} className={inputClass}
                        value={(draft.minimum || []).map((m) => `${m.value} | ${m.label}`).join('\n')}
                        onChange={(e) => update({ minimum: e.target.value.split('\n').filter((l) => l.trim()).map((l) => { const [v, ...rest] = l.split('|'); return { value: v.trim(), label: rest.join('|').trim() }; }) })}
                    />
                </Field>
                <Field label="Tip under the minimum" hint="Wrap words in **double stars** for bold"><input className={inputClass} value={draft.tip || ''} onChange={(e) => update({ tip: e.target.value })} /></Field>
                <Field label="Instructions" hint="Start a block with “# Title”. Lines starting “- ” become bullets.">
                    <textarea rows={8} className={`${inputClass} font-mono text-xs`} value={text} onChange={(e) => parseInstructions(e.target.value)} />
                </Field>
            </div>
        </details>
    );
}

function SectionEditor({ section, draft, onChange, onDelete }) {
    const [openField, setOpenField] = useState(null);
    const [picker, setPicker] = useState(false);
    const dragFrom = useRef(null);
    const fields = section.fields || [];
    const hasScores = fields.some((f) => f.type === 'score');
    const allKeys = useMemo(() => new Set(draft.sections.flatMap((s) => (s.fields || []).map((f) => f.key).filter(Boolean))), [draft]);
    const checkboxes = draft.sections.flatMap((s) => (s.fields || []).filter((f) => f.type === 'checkbox'));

    const setFields = (next) => onChange({ fields: next });
    const updateField = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));

    const addField = (type) => {
        setFields([...fields, defaultField(type, section.key, allKeys)]);
        setPicker(false);
        setOpenField(fields.length);
    };

    return (
        <Panel className="min-w-0">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <Eyebrow>Section {section.key}</Eyebrow>
                    <h2 className="mt-1 text-lg font-bold text-ink-950">{section.title} — fields</h2>
                </div>
                {hasScores && <p className="text-xs text-ink-500">Total auto-calculated from all “Score” fields</p>}
            </div>

            <details className="mt-4 rounded-xl border border-border bg-ink-50/50 p-4">
                <summary className="cursor-pointer text-sm font-semibold text-ink-900">Section details</summary>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Key" hint="Short letter or number shown to students, e.g. B"><input className={inputClass} value={section.key} maxLength={4} onChange={(e) => onChange({ key: e.target.value.toUpperCase() })} /></Field>
                    <Field label="Title" hint="Shown in the part list"><input className={inputClass} value={section.title} onChange={(e) => onChange({ title: e.target.value })} /></Field>
                    {draft.layout === 'workbook' ? (
                        <>
                            <Field label="Part label"><input className={inputClass} value={section.part_label || ''} placeholder={`Part ${section.key} · ${section.title}`} onChange={(e) => onChange({ part_label: e.target.value })} /></Field>
                            <Field label="Page heading"><input className={inputClass} value={section.heading || ''} placeholder={section.title} onChange={(e) => onChange({ heading: e.target.value })} /></Field>
                        </>
                    ) : (
                        <Field label="Eyebrow" hint="e.g. Step 3 · Optional"><input className={inputClass} value={section.eyebrow || ''} onChange={(e) => onChange({ eyebrow: e.target.value })} /></Field>
                    )}
                    <div className="md:col-span-2"><Field label="Description"><textarea rows={2} className={inputClass} value={section.description || ''} onChange={(e) => onChange({ description: e.target.value })} /></Field></div>
                    <div className="md:col-span-2"><Field label="Footnote"><input className={inputClass} value={section.footnote || ''} onChange={(e) => onChange({ footnote: e.target.value })} /></Field></div>
                    {hasScores && (
                        <label className="flex items-center gap-2 text-sm text-ink-800 md:col-span-2">
                            <input type="checkbox" checked={Boolean(section.show_score_panel)} onChange={(e) => onChange({ show_score_panel: e.target.checked })} className="rounded border-ink-300 text-brand-700" />
                            Show the live score panel beside this section
                        </label>
                    )}
                </div>
                <button type="button" onClick={onDelete} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:underline">
                    <TrashIcon className="h-4 w-4" /> Delete section
                </button>
            </details>

            <div className="mt-4 overflow-x-auto">
                <div className="min-w-[560px]">
                    <div className="grid grid-cols-[24px_minmax(0,1fr)_150px_80px_80px_40px] gap-3 px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                        <span />
                        <span>Field label</span>
                        <span>Type</span>
                        <span>Required</span>
                        <span>Allow N/A</span>
                        <span />
                    </div>
                    <ul className="space-y-2">
                        {fields.map((field, i) => {
                            const input = isInputField(field);
                            return (
                                <li
                                    key={field.key || `d${i}`}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => { setFields(move(fields, dragFrom.current, i)); dragFrom.current = null; setOpenField(null); }}
                                    className="rounded-xl border border-border bg-white"
                                >
                                    <div className="grid grid-cols-[24px_minmax(0,1fr)_150px_80px_80px_40px] items-center gap-3 px-3 py-2.5">
                                        <span draggable onDragStart={() => { dragFrom.current = i; }} className="cursor-grab select-none text-ink-300" aria-hidden="true">⋮⋮</span>
                                        <span className="truncate text-sm font-semibold text-ink-900">{field.label || field.title || 'Untitled'}</span>
                                        <span><span className="rounded-md bg-ink-100 px-2 py-1 text-[11px] font-medium text-ink-700">{TYPE_LABEL[field.type] || field.type}</span></span>
                                        {input ? (
                                            <>
                                                <label className="flex items-center gap-1.5 text-xs text-ink-700">
                                                    <input type="checkbox" checked={Boolean(field.required)} onChange={(e) => updateField(i, { required: e.target.checked })} className="rounded border-ink-300 text-brand-700" /> Yes
                                                </label>
                                                <label className={`flex items-center gap-1.5 text-xs text-ink-700 ${['checkbox', 'checklist', 'file_upload'].includes(field.type) ? 'invisible' : ''}`}>
                                                    <input type="checkbox" checked={Boolean(field.allow_na)} onChange={(e) => updateField(i, { allow_na: e.target.checked })} className="rounded border-ink-300 text-brand-700" /> Yes
                                                </label>
                                            </>
                                        ) : <><span /><span /></>}
                                        <button
                                            type="button" aria-label={`Edit ${field.label || field.title}`} aria-expanded={openField === i}
                                            onClick={() => setOpenField(openField === i ? null : i)}
                                            className={`flex h-8 w-8 items-center justify-center rounded-lg border ${openField === i ? 'border-brand-600 text-brand-700' : 'border-border text-ink-600'} hover:border-ink-400`}
                                        >
                                            <EllipsisHorizontalIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    {openField === i && (
                                        <FieldEditor
                                            field={field}
                                            checkboxes={checkboxes.filter((c) => c.key !== field.key)}
                                            onChange={(patch) => updateField(i, patch)}
                                            onMove={(dir) => { setFields(move(fields, i, i + dir)); setOpenField(i + dir); }}
                                            onDelete={() => { setFields(fields.filter((_, j) => j !== i)); setOpenField(null); }}
                                            first={i === 0}
                                            last={i === fields.length - 1}
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            <div className="relative mt-3">
                <button type="button" onClick={() => setPicker((p) => !p)} aria-expanded={picker} className="rounded-lg border border-border bg-white px-4 py-2 text-sm font-semibold text-ink-900 hover:border-ink-400">
                    + Add field
                </button>
                {picker && (
                    <div className="absolute left-0 z-20 mt-2 grid w-[min(36rem,calc(100vw-3rem))] grid-cols-1 gap-1 rounded-2xl border border-border bg-white p-2 shadow-soft sm:grid-cols-2">
                        {FIELD_TYPES.map((t) => (
                            <button key={t.type} type="button" onClick={() => addField(t.type)} className="rounded-lg px-3 py-2.5 text-left hover:bg-ink-50">
                                <span className="block text-sm font-semibold text-ink-900">{t.label}</span>
                                <span className="block text-xs text-ink-500">{t.hint}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Panel>
    );
}

function FieldEditor({ field, checkboxes, onChange, onMove, onDelete, first, last }) {
    const t = field.type;
    return (
        <div className="space-y-3 border-t border-border bg-ink-50/50 px-4 py-4">
            {t === 'heading' && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Heading"><input className={inputClass} value={field.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></Field>
                    <Field label="Eyebrow"><input className={inputClass} value={field.eyebrow || ''} onChange={(e) => onChange({ eyebrow: e.target.value })} placeholder="e.g. Part B · Section 8" /></Field>
                    <div className="md:col-span-2"><Field label="Description"><textarea rows={2} className={inputClass} value={field.description || ''} onChange={(e) => onChange({ description: e.target.value })} /></Field></div>
                </div>
            )}
            {t === 'info' && (
                <>
                    <Field label="Title"><input className={inputClass} value={field.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></Field>
                    <Field label="Text"><textarea rows={3} className={inputClass} value={field.body || ''} onChange={(e) => onChange({ body: e.target.value })} /></Field>
                    <Field label="Bullets" hint="One per line"><textarea rows={3} className={inputClass} value={(field.bullets || []).join('\n')} onChange={(e) => onChange({ bullets: e.target.value.split('\n').filter((x) => x.trim()) })} /></Field>
                </>
            )}
            {isInputField(field) && (
                <>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="md:col-span-2"><Field label="Question / label"><input className={inputClass} value={field.label || ''} onChange={(e) => onChange({ label: e.target.value })} /></Field></div>
                        <Field label="Help text"><input className={inputClass} value={field.help || ''} onChange={(e) => onChange({ help: e.target.value })} /></Field>
                        {['short_text', 'long_text'].includes(t) && <Field label="Placeholder"><input className={inputClass} value={field.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} /></Field>}
                        {t === 'score' && <Field label="Short label (score panel)"><input className={inputClass} value={field.short_label || ''} onChange={(e) => onChange({ short_label: e.target.value })} /></Field>}
                        {t === 'number' && (
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Minimum"><input type="number" className={inputClass} value={field.min ?? ''} onChange={(e) => onChange({ min: e.target.value === '' ? undefined : Number(e.target.value) })} /></Field>
                                <Field label="Maximum"><input type="number" className={inputClass} value={field.max ?? ''} onChange={(e) => onChange({ max: e.target.value === '' ? undefined : Number(e.target.value) })} /></Field>
                            </div>
                        )}
                        <Field label="Only show when this is ticked">
                            <select className={inputClass} value={field.depends_on || ''} onChange={(e) => onChange({ depends_on: e.target.value || undefined })}>
                                <option value="">Always show</option>
                                {checkboxes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </Field>
                        {['short_text', 'number', 'date'].includes(t) && (
                            <label className="flex items-center gap-2 self-end pb-2 text-sm text-ink-800">
                                <input type="checkbox" checked={field.width === 'half'} onChange={(e) => onChange({ width: e.target.checked ? 'half' : undefined })} className="rounded border-ink-300 text-brand-700" />
                                Half width (pairs with the next half-width field)
                            </label>
                        )}
                    </div>

                    {t === 'score' && (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            {[1, 3, 5].map((n) => (
                                <Field key={n} label={`Level ${n} description`}>
                                    <input className={inputClass} value={(field.levels || {})[n] || ''} onChange={(e) => onChange({ levels: { ...(field.levels || {}), [n]: e.target.value } })} />
                                </Field>
                            ))}
                        </div>
                    )}

                    {t === 'single_choice' && <OptionsEditor field={field} onChange={onChange} />}

                    {t === 'checklist' && (
                        <Field label="Items" hint="One per line — every item must be ticked to submit">
                            <textarea rows={6} className={inputClass} value={(field.items || []).join('\n')} onChange={(e) => onChange({ items: e.target.value.split('\n').filter((x) => x.trim()) })} />
                        </Field>
                    )}

                    {t === 'file_upload' && (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Field label="Accepted files">
                                <select className={inputClass} value={field.accept || 'image_pdf'} onChange={(e) => onChange({ accept: e.target.value })}>
                                    <option value="image">Images (JPG, PNG)</option>
                                    <option value="image_pdf">Images and PDF</option>
                                </select>
                            </Field>
                            <Field label="Maximum files"><input type="number" min={1} max={20} className={inputClass} value={field.max_files || 5} onChange={(e) => onChange({ max_files: Number(e.target.value) })} /></Field>
                            <Field label="Consent tick label" hint="Leave empty for no consent tick"><input className={inputClass} value={field.consent_label || ''} onChange={(e) => onChange({ consent_label: e.target.value })} /></Field>
                            <Field label="Consent applies to">
                                <select className={inputClass} value={field.consent_scope || 'each'} onChange={(e) => onChange({ consent_scope: e.target.value })}>
                                    <option value="each">Each file separately</option>
                                    <option value="all">All files with one tick</option>
                                </select>
                            </Field>
                        </div>
                    )}

                    {t === 'checkbox' && (
                        <label className="flex items-center gap-2 text-sm text-ink-800">
                            <input type="checkbox" checked={field.display === 'banner'} onChange={(e) => onChange({ display: e.target.checked ? 'banner' : undefined })} className="rounded border-ink-300 text-brand-700" />
                            Show as a highlighted banner
                        </label>
                    )}
                    <p className="text-[11px] text-ink-400">Field key: <code>{field.key}</code></p>
                </>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
                <button type="button" disabled={first} onClick={() => onMove(-1)} className="btn-secondary inline-flex items-center gap-1 disabled:opacity-40"><ArrowUpIcon className="h-3.5 w-3.5" /> Up</button>
                <button type="button" disabled={last} onClick={() => onMove(1)} className="btn-secondary inline-flex items-center gap-1 disabled:opacity-40"><ArrowDownIcon className="h-3.5 w-3.5" /> Down</button>
                <button type="button" onClick={onDelete} className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:underline"><TrashIcon className="h-4 w-4" /> Delete field</button>
            </div>
        </div>
    );
}

function OptionsEditor({ field, onChange }) {
    const options = field.options || [];
    const setOptions = (next) => onChange({ options: next });
    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-4">
                <span className="text-xs font-semibold text-ink-800">Options</span>
                <label className="flex items-center gap-1.5 text-xs text-ink-700">
                    Columns
                    <select className="rounded-md border border-border px-2 py-1 text-xs" value={field.columns || 2} onChange={(e) => onChange({ columns: Number(e.target.value), display: 'cards' })}>
                        {[2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                </label>
                <label className="flex items-center gap-1.5 text-xs text-ink-700">
                    <input type="checkbox" checked={Boolean(field.glance)} onChange={(e) => onChange({ glance: e.target.checked })} className="rounded border-ink-300 text-brand-700" />
                    Show in mentor “at a glance”
                </label>
            </div>
            {options.map((opt, i) => (
                <div key={i} className="grid grid-cols-[70px_minmax(0,1fr)_minmax(0,1fr)_32px] gap-2">
                    <input aria-label="Value" className={inputClass} value={opt.value} onChange={(e) => setOptions(options.map((o, j) => (j === i ? { ...o, value: e.target.value } : o)))} />
                    <input aria-label="Label" className={inputClass} value={opt.label} placeholder="Label" onChange={(e) => setOptions(options.map((o, j) => (j === i ? { ...o, label: e.target.value } : o)))} />
                    <input aria-label="Description" className={inputClass} value={opt.description || ''} placeholder="Description" onChange={(e) => setOptions(options.map((o, j) => (j === i ? { ...o, description: e.target.value } : o)))} />
                    <button type="button" aria-label="Remove option" onClick={() => setOptions(options.filter((_, j) => j !== i))} className="text-ink-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                </div>
            ))}
            <button
                type="button"
                onClick={() => setOptions([...options, { value: String.fromCharCode(65 + options.length), label: `Option ${String.fromCharCode(65 + options.length)}`, description: '' }])}
                className="text-xs font-semibold text-brand-700 hover:underline"
            >
                + Add option
            </button>
        </div>
    );
}
